import type { OrgType } from '../types'
import type { TrialDaysByOrgType } from '../hooks/use-register-page'

export type RoleSelectionStepProps = {
  onSelect: (orgType: OrgType) => void
  trialDaysByOrgType: TrialDaysByOrgType
  showCrew?: boolean
  proCrew?: boolean
}

type RoleOption = {
  orgType: OrgType
  title: string
  description: string
  pricing: string
  trialBadge: string | null
  headerBg: string
  heroImage: string
  testId: string
}

function buildCrewOption(
  trialDaysByOrgType: TrialDaysByOrgType,
  proCrew: boolean,
): RoleOption {
  if (proCrew) {
    return {
      orgType: 'crew',
      title: 'Crew',
      description:
        'A group of hunters who log hunts and plan together. Creating a crew is free, but every crew member needs BlindBook Pro.',
      pricing: 'Free to create',
      trialBadge: null,
      headerBg: 'rgba(74, 111, 127, 0.35)',
      heroImage: '/crew_hero.jpg',
      testId: 'role-selection-crew',
    }
  }
  return {
    orgType: 'crew',
    title: 'Hunt Crew',
    description:
      'For small groups hunting together. 6 seats included, up to 15 total. Track hunts, share a calendar, and coordinate logistics without management overhead.',
    pricing: 'From $49/mo · $399/yr',
    trialBadge: trialDaysByOrgType.crew
      ? `${trialDaysByOrgType.crew}-day free trial`
      : 'Free trial',
    headerBg: 'rgba(74, 111, 127, 0.35)',
    heroImage: '/crew_hero.jpg',
    testId: 'role-selection-crew',
  }
}

function buildRoleOptions(
  trialDaysByOrgType: TrialDaysByOrgType,
  showCrew: boolean,
  proCrew: boolean,
): RoleOption[] {
  const crewOptions = showCrew ? [buildCrewOption(trialDaysByOrgType, proCrew)] : []
  return [
    ...crewOptions,
    {
      orgType: 'club',
      title: 'Club Owner or Manager',
      description:
        'Create a club that streamlines member management, blind rotations, harvest compliance, and club operations — all from one powerful platform built for waterfowl clubs.',
      pricing: 'From $125/mo · $1,200/yr',
      trialBadge: trialDaysByOrgType.club
        ? `${trialDaysByOrgType.club}-day free trial`
        : 'Free trial',
      headerBg: 'rgba(67, 198, 183, 0.18)',
      heroImage: '/club_hero.jpg',
      testId: 'role-selection-club',
    },
    {
      orgType: 'outfitter',
      title: 'Outfitter Owner or Manager',
      description:
        'From first booking to final tip — manage every customer, guide, and season with the platform built for professional waterfowl outfitters.',
      pricing: 'From $179/mo · $1,750/yr',
      trialBadge: 'Free through September 1st, 2026',
      headerBg: 'rgba(242, 153, 74, 0.22)',
      heroImage: '/outfitter_hero.jpg',
      testId: 'role-selection-outfitter',
    },
  ]
}

export function RoleSelectionStep({
  onSelect,
  trialDaysByOrgType,
  showCrew = true,
  proCrew = false,
}: RoleSelectionStepProps) {
  const options = buildRoleOptions(trialDaysByOrgType, showCrew, proCrew)
  return (
    <div className="flex flex-col gap-3" data-testid="role-selection">
      {options.map((option) => (
        <button
          key={option.orgType}
          type="button"
          onClick={() => onSelect(option.orgType)}
          data-testid={option.testId}
          className="relative w-full overflow-hidden rounded-xl border bg-card text-left transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <div
            aria-hidden
            className="pointer-events-none absolute inset-0 bg-cover bg-center opacity-[0.12]"
            style={{ backgroundImage: `url(${option.heroImage})` }}
          />
          <div className="relative">
            <div
              className="px-5 py-2.5 text-sm font-bold text-foreground"
              style={{ backgroundColor: option.headerBg }}
            >
              {option.title}
            </div>
            <div className="flex flex-col gap-3 px-5 py-4">
              <p className="text-sm leading-relaxed text-foreground">{option.description}</p>
              <div className="flex flex-col gap-1.5">
                <span className="text-sm font-semibold text-primary">{option.pricing}</span>
                {option.trialBadge ? (
                  <span className="self-start rounded-md bg-primary/15 px-2.5 py-1 text-xs font-semibold text-primary">
                    {option.trialBadge}
                  </span>
                ) : null}
              </div>
            </div>
          </div>
        </button>
      ))}
    </div>
  )
}
